import { useCallback, useEffect, useRef, useState } from 'react';
import { PlayerCard } from './components/PlayerCard';
import { ChatPanel, isChatCapable } from './components/ChatPanel';
import { Diagnostics } from './components/Diagnostics';
import { parseSourceUrl, defaultTitle, sourceKey } from './lib/parseUrl';
import { assumeSingleAudio } from './lib/platform';
import type { Item, LayoutMode, PlaybackState } from './lib/types';

const STORAGE_KEY = 'multi-stream:items:v2';
const LAYOUT_KEY = 'multi-stream:layout:v1';
const BAR_KEY = 'multi-stream:bar:v1';

const LAYOUTS: { mode: LayoutMode; label: string; hint: string }[] = [
  { mode: 'auto', label: '自動', hint: '画面幅に合わせる' },
  { mode: 1, label: '縦', hint: '1 列（縦並び）' },
  { mode: 2, label: '2列', hint: '2 列（2×2, 2×3 など）' },
  { mode: 3, label: '3列', hint: '3 列（3×2 など）' },
  { mode: 4, label: '4列', hint: '4 列' },
  { mode: 'row', label: '横', hint: '全部を横 1 列に' },
];

function loadItems(): Item[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Item[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function loadLayout(): LayoutMode {
  try {
    const raw = localStorage.getItem(LAYOUT_KEY);
    if (raw === 'auto' || raw === 'row') return raw;
    const n = Number(raw);
    return n === 1 || n === 2 || n === 3 || n === 4 ? n : 'auto';
  } catch {
    return 'auto';
  }
}

export default function App() {
  const [items, setItems] = useState<Item[]>(loadItems);
  const [barOpen, setBarOpen] = useState(() => {
    try {
      return localStorage.getItem(BAR_KEY) !== 'closed';
    } catch {
      return true;
    }
  });
  const [layout, setLayout] = useState<LayoutMode>(loadLayout);
  const [chatFor, setChatFor] = useState<string | null>(null);
  const [url, setUrl] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [observedInterruptions, setObservedInterruptions] = useState(0);

  const playing = useRef<Map<string, PlaybackState>>(new Map());
  const lastAudioGrant = useRef<{ id: string; at: number } | null>(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    localStorage.setItem(LAYOUT_KEY, String(layout));
  }, [layout]);

  useEffect(() => {
    localStorage.setItem(BAR_KEY, barOpen ? 'open' : 'closed');
  }, [barOpen]);

  const add = (e: React.FormEvent) => {
    e.preventDefault();
    const { source, error } = parseSourceUrl(url);
    if (!source) {
      setAddError(error ?? '対応していない URL です');
      return;
    }
    setAddError(null);
    setUrl('');
    const key = sourceKey(source);
    setItems((prev) => {
      if (prev.some((i) => sourceKey(i.source) === key)) return prev;
      return [
        ...prev,
        {
          id: `${key}:${Date.now()}`,
          source,
          title: defaultTitle(source),
          muted: true,
        },
      ];
    });
  };

  const toggleHidden = (id: string) =>
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, hidden: !i.hidden } : i)));

  const remove = (id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
    setChatFor((cur) => (cur === id ? null : cur));
  };

  /**
   * 音声の割り当て。
   * iOS は WebKit が「音を出せる video は同時に 1 つ」を制限するため、常に排他。
   * それ以外の環境では複数を同時に鳴らせるのでトグルにする。
   *
   * この排他は WebKit の制限そのものではなく、こちらが先回りしているもの。
   * 実際には制限が再生開始時にしか評価されないので、プレイヤー純正のスピーカー
   * ボタンを使えば 2 つ同時に鳴らせる (docs/feasibility.md §2.6)。ただし順番に
   * 依存し、再生が始まり直すと片方が止まるため、排他はこのまま維持する。
   */
  const toggleAudio = (id: string) => {
    lastAudioGrant.current = { id, at: Date.now() };
    setItems((prev) => {
      const target = prev.find((i) => i.id === id);
      const turningOn = target?.muted !== false;
      if (assumeSingleAudio) {
        return prev.map((i) => ({ ...i, muted: !(turningOn && i.id === id) }));
      }
      return prev.map((i) => (i.id === id ? { ...i, muted: !turningOn } : i));
    });
  };

  const muteAll = () => setItems((prev) => prev.map((i) => ({ ...i, muted: true })));

  /**
   * 実測: 「別タイルへ音声を渡した直後に、再生中だったタイルが自動停止した」
   * ことを観測したら、この端末が複数音声を許可していない証拠として記録する。
   */
  const onPlaybackState = useCallback((id: string, state: PlaybackState) => {
    const prev = playing.current.get(id);
    playing.current.set(id, state);
    const grant = lastAudioGrant.current;
    if (prev === 'playing' && state === 'paused' && grant && grant.id !== id && Date.now() - grant.at < 4000) {
      setObservedInterruptions((n) => n + 1);
    }
  }, []);

  const chatItem = items.find((i) => i.id === chatFor) ?? null;
  const cols = layout === 'row' ? Math.max(1, items.length) : layout;

  return (
    <div className="app" data-bar={barOpen ? 'open' : 'closed'}>
      {!barOpen && (
        <button
          className="bar-show"
          onClick={() => setBarOpen(true)}
          aria-label="操作バーを表示"
          title="操作バーを表示"
        >
          ▾
        </button>
      )}

      <header className="bar" hidden={!barOpen}>
        <form className="bar-add" onSubmit={add}>
          <input
            id="url-input"
            type="url"
            inputMode="url"
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            placeholder="YouTube / Twitch の URL を貼り付け"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <button type="submit" aria-label="追加">
            ＋
          </button>
          <button
            type="button"
            className="bar-hide"
            onClick={() => setBarOpen(false)}
            aria-label="操作バーを隠す"
            title="操作バーを隠す"
          >
            ▴
          </button>
        </form>

        <div className="bar-rows">
        {items.length > 0 && (
          <div className="bar-row">
            <span className="bar-label">音声</span>
            <button className="chip" onClick={muteAll} aria-label="すべてミュート">
              🔇
            </button>
            {items.map((item, i) => (
              <button
                key={item.id}
                className={`chip audio-pick ${item.muted ? '' : 'on'}`}
                data-id={item.id}
                aria-pressed={!item.muted}
                onClick={() => toggleAudio(item.id)}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}

        {items.some(isChatCapable) && (
          <div className="bar-row">
            <span className="bar-label">チャット</span>
            <button
              className={`chip ${chatFor === null ? 'on' : ''}`}
              onClick={() => setChatFor(null)}
            >
              なし
            </button>
            {items.map((item, i) =>
              isChatCapable(item) ? (
                <button
                  key={item.id}
                  className={`chip chat-pick ${chatFor === item.id ? 'on' : ''}`}
                  data-id={item.id}
                  aria-pressed={chatFor === item.id}
                  onClick={() => setChatFor(chatFor === item.id ? null : item.id)}
                >
                  {i + 1}
                </button>
              ) : null,
            )}
          </div>
        )}

        {items.length > 0 && (
          <div className="bar-row">
            <span className="bar-label">表示</span>
            {items.map((item, i) => (
              <button
                key={item.id}
                className={`chip show-pick ${item.hidden ? '' : 'on'}`}
                data-id={item.id}
                aria-pressed={!item.hidden}
                title={item.hidden ? '映像を表示する' : '映像を隠して音だけにする'}
                onClick={() => toggleHidden(item.id)}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}

        <div className="bar-row">
          <span className="bar-label">配置</span>
          {LAYOUTS.map((l) => (
            <button
              key={String(l.mode)}
              className={`chip layout-pick ${layout === l.mode ? 'on' : ''}`}
              data-mode={String(l.mode)}
              title={l.hint}
              onClick={() => setLayout(l.mode)}
            >
              {l.label}
            </button>
          ))}
        </div>
        </div>

        {addError && <p className="bar-error">{addError}</p>}
      </header>

      <main className="stage" data-chat={chatItem ? 'on' : 'off'}>
        <div
          className="grid"
          data-cols={String(layout)}
          style={typeof cols === 'number' ? ({ '--cols': cols } as React.CSSProperties) : undefined}
        >
          {items.map((item, i) => (
            <PlayerCard
              key={item.id}
              item={item}
              index={i + 1}
              hasAudio={!item.muted}
              hidden={Boolean(item.hidden)}
              onToggleHidden={() => toggleHidden(item.id)}
              onRemove={() => remove(item.id)}
              onPlaybackState={(s) => onPlaybackState(item.id, s)}
            />
          ))}
        </div>

        {chatItem && (
          <aside className="chatdock">
            <ChatPanel item={chatItem} onClose={() => setChatFor(null)} />
          </aside>
        )}
      </main>

      {items.length === 0 && (
        <p className="empty">上の入力欄に YouTube / Twitch の URL を貼って ＋ を押してください。</p>
      )}

      <footer className="app-foot">
        <Diagnostics observedInterruptions={observedInterruptions} />
      </footer>
    </div>
  );
}
